import React, { useState, useEffect } from 'react';
import { API_BASE } from '../utils/api';

interface AutomationRule {
  id: number;
  name: string;
  description: string;
  trigger_type: 'workflow_created' | 'stage_changed' | 'task_completed' | 'sla_breach' | 'time_based';
  trigger_conditions: Record<string, any>;
  actions: AutomationAction[];
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

interface AutomationAction {
  id: number;
  action_type: 'assign_user' | 'change_priority' | 'send_notification' | 'create_task' | 'advance_stage' | 'send_email';
  parameters: Record<string, any>;
  delay_minutes?: number;
}

interface WorkflowAutomationProps {
  onClose: () => void;
}

const WorkflowAutomation: React.FC<WorkflowAutomationProps> = ({ onClose }) => {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'workflow_created' as 'workflow_created' | 'stage_changed' | 'task_completed' | 'sla_breach' | 'time_based',
    trigger_conditions: {} as Record<string, any>,
    is_active: true,
    priority: 1
  });

  const [actions, setActions] = useState<AutomationAction[]>([]);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_BASE}/workflow/automation/rules`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRules(Array.isArray(data) ? data : (data?.data || []));
      }
    } catch (error) {
      console.error('Error fetching automation rules:', error);
      setError('Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      trigger_type: 'workflow_created',
      trigger_conditions: {},
      is_active: true,
      priority: 1
    });
    setActions([]);
    setShowCreateModal(true);
  };

  const handleEditRule = (rule: AutomationRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description,
      trigger_type: rule.trigger_type,
      trigger_conditions: rule.trigger_conditions,
      is_active: rule.is_active,
      priority: rule.priority
    });
    setActions(rule.actions || []);
    setShowCreateModal(true);
  };

  const handleSaveRule = async () => {
    if (!formData.name.trim()) {
      setError('Rule name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const payload = {
        ...formData,
        actions
      };

      const url = editingRule 
        ? `${API_BASE}/workflow/automation/rules/${editingRule.id}`
        : `${API_BASE}/workflow/automation/rules`;
      
      const method = editingRule ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccess(editingRule ? 'Rule updated successfully' : 'Rule created successfully');
        await fetchRules();
        setShowCreateModal(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to save rule');
      }
    } catch (error) {
      console.error('Error saving rule:', error);
      setError('Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this automation rule?')) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_BASE}/workflow/automation/rules/${ruleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('Rule deleted successfully');
        await fetchRules();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete rule');
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      setError('Failed to delete rule');
    }
  };

  const addAction = () => {
    setActions(prev => [...prev, {
      id: Date.now(),
      action_type: 'assign_user',
      parameters: {},
      delay_minutes: 0
    }]);
  };

  const updateAction = (index: number, field: keyof AutomationAction, value: any) => {
    setActions(prev => prev.map((action, i) => 
      i === index ? { ...action, [field]: value } : action
    ));
  };

  const removeAction = (index: number) => {
    setActions(prev => prev.filter((_, i) => i !== index));
  };

  const getTriggerDescription = (rule: AutomationRule) => {
    switch (rule.trigger_type) {
      case 'workflow_created':
        return 'When a new workflow is created';
      case 'stage_changed':
        return `When workflow moves to stage: ${rule.trigger_conditions.stage || 'any'}`;
      case 'task_completed':
        return `When task is completed: ${rule.trigger_conditions.task_type || 'any'}`;
      case 'sla_breach':
        return 'When SLA deadline is approaching or breached';
      case 'time_based':
        return `Time-based trigger: ${rule.trigger_conditions.schedule || 'daily'}`;
      default:
        return 'Unknown trigger';
    }
  };

  const getActionDescription = (action: AutomationAction) => {
    switch (action.action_type) {
      case 'assign_user':
        return `Assign to user: ${action.parameters.user_id || 'selected user'}`;
      case 'change_priority':
        return `Change priority to: ${action.parameters.priority || 'normal'}`;
      case 'send_notification':
        return `Send notification: ${action.parameters.message || 'custom message'}`;
      case 'create_task':
        return `Create task: ${action.parameters.title || 'new task'}`;
      case 'advance_stage':
        return `Advance to stage: ${action.parameters.stage || 'next stage'}`;
      case 'send_email':
        return `Send email to: ${action.parameters.recipient || 'assigned user'}`;
      default:
        return 'Unknown action';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Workflow Automation</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600">Create automation rules to streamline workflow processes</p>
          <button
            onClick={handleCreateRule}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Rule
          </button>
        </div>

        {/* Rules List */}
        <div className="space-y-4">
          {rules.map((rule) => (
            <div key={rule.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{rule.name}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Priority {rule.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{rule.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="font-medium">Trigger:</span> {getTriggerDescription(rule)}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">Actions:</span> {rule.actions?.length || 0} action(s)
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEditRule(rule)}
                    className="text-indigo-600 hover:text-indigo-900 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteRule(rule.id)}
                    className="text-red-600 hover:text-red-900 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Actions List */}
              {rule.actions && rule.actions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Actions:</h4>
                  <div className="space-y-1">
                    {rule.actions.map((action, index) => (
                      <div key={index} className="text-sm text-gray-600 flex items-center">
                        <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                        {getActionDescription(action)}
                        {action.delay_minutes && action.delay_minutes > 0 && (
                          <span className="ml-2 text-xs text-gray-500">
                            (after {action.delay_minutes} minutes)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {rules.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <p className="text-gray-500 mb-4">No automation rules found</p>
            <button
              onClick={handleCreateRule}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Create Your First Rule
            </button>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingRule ? 'Edit Automation Rule' : 'Create Automation Rule'}
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rule Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter rule name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={3}
                    placeholder="Enter rule description"
                  />
                </div>

                {/* Trigger Configuration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger Type
                  </label>
                  <select
                    value={formData.trigger_type}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      trigger_type: e.target.value as any,
                      trigger_conditions: {}
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="workflow_created">Workflow Created</option>
                    <option value="stage_changed">Stage Changed</option>
                    <option value="task_completed">Task Completed</option>
                    <option value="sla_breach">SLA Breach</option>
                    <option value="time_based">Time-based</option>
                  </select>
                </div>

                {/* Actions */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Actions
                    </label>
                    <button
                      onClick={addAction}
                      className="text-sm text-indigo-600 hover:text-indigo-900"
                    >
                      + Add Action
                    </button>
                  </div>

                  <div className="space-y-4">
                    {actions.map((action, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Action Type
                            </label>
                            <select
                              value={action.action_type}
                              onChange={(e) => updateAction(index, 'action_type', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value="assign_user">Assign User</option>
                              <option value="change_priority">Change Priority</option>
                              <option value="send_notification">Send Notification</option>
                              <option value="create_task">Create Task</option>
                              <option value="advance_stage">Advance Stage</option>
                              <option value="send_email">Send Email</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Delay (minutes)
                            </label>
                            <input
                              type="number"
                              value={action.delay_minutes || 0}
                              onChange={(e) => updateAction(index, 'delay_minutes', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              min="0"
                            />
                          </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={() => removeAction(index)}
                            className="text-red-600 hover:text-red-900 text-sm"
                          >
                            Remove Action
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRule}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowAutomation;

